import { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Tooltip, Modal, message } from 'antd';
import { ExpandOutlined, CodeOutlined, BarChartOutlined, TableOutlined, DeleteOutlined, CopyOutlined, CommentOutlined } from '@ant-design/icons';
import { format } from 'sql-formatter';
import { DashboardWidget as DashboardWidgetType } from '@/types/dashboard';
import { DashboardChartRenderer, ChartRenderer, StatCardRenderer, TableRenderer } from './ChartRenderer';

type ViewMode = 'chart' | 'table' | 'sql';

interface DashboardWidgetProps {
  widget: DashboardWidgetType;
  onDelete: (widgetId: string) => void;
}

const DashboardWidgetComponent = ({ widget, onDelete }: DashboardWidgetProps) => {
  const navigate = useNavigate();
  const { widget_config, title } = widget;
  const chartData = widget_config.chartData;
  const statData = widget_config.statData;
  const sql = widget_config.sql;

  const isAITable = chartData?.type === 'table';
  const [viewMode, setViewMode] = useState<ViewMode>(isAITable ? 'table' : 'chart');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleOpenChat = () => {
    // Navigate to /query (no session ID = fresh chat) with chart context in state
    navigate('/query', {
      state: {
        chartContext: { title, sql, chartData, statData }
      }
    });
  };

  const handleCopySQL = () => {
    if (sql) {
      navigator.clipboard.writeText(format(sql, { language: 'postgresql' }));
      message.success('SQL copied to clipboard');
    }
  };

  const renderChartContent = () => {
    if (!chartData) return null;

    if (viewMode === 'sql' && sql) {
      return (
        <div className="bg-gray-900 text-gray-100 rounded overflow-auto" style={{ height: '100%', position: 'absolute', inset: 0 }}>
          <Tooltip title="Copy SQL">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopySQL}
              className="text-gray-400 hover:text-white"
              style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10, color: '#9CA3AF' }}
            />
          </Tooltip>
          <pre className="p-4 text-sm whitespace-pre-wrap" style={{ height: '100%' }}>
            <code>{format(sql, { language: 'postgresql' })}</code>
          </pre>
        </div>
      );
    }

    if (viewMode === 'table') {
      return (
        <div className="overflow-auto" style={{ position: 'absolute', inset: 0 }}>
          <TableRenderer data={chartData.data} chartData={chartData} />
        </div>
      );
    }

    // viewMode === 'chart'
    return <DashboardChartRenderer chartData={chartData} />;
  };

  return (
    <div className="h-full">
      <Card
        className="h-full group relative shadow-sm hover:shadow-md transition-shadow"
        bodyStyle={{ height: '100%', padding: '20px', display: 'flex', flexDirection: 'column' }}
      >
        {/* Hover buttons */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 z-10">
          {/* Expand - only in chart mode */}
          {viewMode === 'chart' && !isAITable && (
            <Tooltip title="Expand">
              <Button
                type="text"
                size="small"
                icon={<ExpandOutlined />}
                onClick={() => setIsExpanded(true)}
                className="bg-white shadow-sm hover:shadow-md"
              />
            </Tooltip>
          )}
          {/* View toggles for chart widgets */}
          {chartData && sql && (
            <>
              {viewMode === 'chart' && (
                <Tooltip title="Show Table">
                  <Button
                    type="text"
                    size="small"
                    icon={<TableOutlined />}
                    onClick={() => setViewMode('table')}
                    className="bg-white shadow-sm hover:shadow-md"
                  />
                </Tooltip>
              )}
              {viewMode === 'table' && (
                <>
                  <Tooltip title="Show SQL">
                    <Button
                      type="text"
                      size="small"
                      icon={<CodeOutlined />}
                      onClick={() => setViewMode('sql')}
                      className="bg-white shadow-sm hover:shadow-md"
                    />
                  </Tooltip>
                  {!isAITable && (
                    <Tooltip title="Show Chart">
                      <Button
                        type="text"
                        size="small"
                        icon={<BarChartOutlined />}
                        onClick={() => setViewMode('chart')}
                        className="bg-white shadow-sm hover:shadow-md"
                      />
                    </Tooltip>
                  )}
                </>
              )}
              {viewMode === 'sql' && (
                <Tooltip title="Show Table">
                  <Button
                    type="text"
                    size="small"
                    icon={<TableOutlined />}
                    onClick={() => setViewMode('table')}
                    className="bg-white shadow-sm hover:shadow-md"
                  />
                </Tooltip>
              )}
            </>
          )}
          {/* Stat card: two-way toggle */}
          {statData && !chartData && sql && (
            <Tooltip title={viewMode === 'sql' ? "Show Stat" : "Show SQL"}>
              <Button
                type="text"
                size="small"
                icon={viewMode === 'sql' ? <BarChartOutlined /> : <CodeOutlined />}
                onClick={() => setViewMode(viewMode === 'sql' ? 'chart' : 'sql')}
                className="bg-white shadow-sm hover:shadow-md"
              />
            </Tooltip>
          )}
          <Tooltip title="Open Chat">
            <Button
              type="text"
              size="small"
              icon={<CommentOutlined />}
              onClick={handleOpenChat}
              className="bg-white shadow-sm hover:shadow-md"
            />
          </Tooltip>
          <Tooltip title="Remove from dashboard">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onDelete(widget.id)}
              className="bg-white shadow-sm hover:shadow-md"
            />
          </Tooltip>
        </div>

        {/* Title */}
        <div className="mb-3">
          <h3 className="text-base font-semibold text-gray-800 truncate pr-24">{title}</h3>
        </div>

        {/* Content */}
        <div className="flex-1" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {statData && !chartData && <StatCardRenderer statData={statData} />}

          {chartData && (
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
              {renderChartContent()}
            </div>
          )}
        </div>
      </Card>

      {/* Expanded Chart Modal */}
      {chartData && !isAITable && (
        <Modal
          title={title}
          open={isExpanded}
          onCancel={() => setIsExpanded(false)}
          width="90%"
          footer={null}
          centered
        >
          <div className="p-4">
            <ChartRenderer chartData={chartData} height={600} />
          </div>
        </Modal>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
// Charts are expensive to render, so we only re-render if the widget config or title changed
// Layout position changes don't affect the chart rendering, so we ignore those
export const DashboardWidget = memo(DashboardWidgetComponent, (prevProps, nextProps) => {
  // Return true if props are equal (to SKIP re-render)
  // Return false if props are different (to trigger re-render)
  
  // Only re-render if widget config or title changed, ignore layout position changes
  const configEqual = JSON.stringify(prevProps.widget.widget_config) === JSON.stringify(nextProps.widget.widget_config);
  const titleEqual = prevProps.widget.title === nextProps.widget.title;
  const idEqual = prevProps.widget.id === nextProps.widget.id;
  
  // Layout size changes should trigger re-render (for responsive charts), but not position
  const sizeEqual = 
    prevProps.widget.layout.w === nextProps.widget.layout.w &&
    prevProps.widget.layout.h === nextProps.widget.layout.h;
  
  return configEqual && titleEqual && idEqual && sizeEqual;
});
